import { breakpoints, mediaQueries } from '../fundamentals/breakpoints/breakpoints';
import { generateThemeColours } from '../fundamentals/colours/colour';
import { blurs, IBlurs } from '../fundamentals/blurs/blurs';
import { fonts } from '../fundamentals/typography/typography';
import { eoraSpaces, spaces } from '../fundamentals/spaces/space';
import { generateShadows } from '../fundamentals/shadows/shadows';
import { generateBorderRadii } from '../fundamentals/sizes/borderRadius';
import { sizes } from '../fundamentals/sizes/sizes';
import { DeepPartial } from '../common/interfaces';
import { BrandColours, MainBrandColours } from '../fundamentals/colours/colourInterfaces';
import { IEoraThemeColours } from '../fundamentals/colours/eoraColourUtils';
import { BaseColours } from '../fundamentals/colours/baseColours';
import { MainBrandColour } from '../fundamentals/colours/variants';

export interface IThemeLogoVariant {
  color: string;
  black?: string;
  white?: string;
  accent?: string;
  textLight?: string;
  bounding?: string;
  boundingWhite?: string;
  noBounding?: string;
  noBoundingWhite?: string;
}

export interface IThemeLogoPlacements {
  header: IThemeLogoVariant;
  mark: IThemeLogoVariant; // square ones to be used as header logo in mobile
  footer: IThemeLogoVariant;
  whiteLabel: IThemeLogoVariant;
  cmcMarketsLogo?: IThemeLogoVariant;
  cmcInvestLogo?: IThemeLogoVariant;
  cmcInvestWhiteLogo?: IThemeLogoVariant;
}

/**
 * Config for login/reset password etc. screens
 */
export interface IPublicSiteConfig {
  variant?: MainBrandColour;
  backgroundImage?: string;
  watermarkImage?: string;
}

export interface ITheme extends ReturnType<typeof generateThemeColours>, ReturnType<typeof generateBorderRadii> {
  name: string;
  blurs: IBlurs;
  fonts: typeof fonts;
  spaces: typeof spaces;
  eoraSpaces: typeof eoraSpaces;
  breakpoints: typeof breakpoints;
  mediaQueries: typeof mediaQueries;
  sizes: typeof sizes;
  shadows: ReturnType<typeof generateShadows>;
  hasGlobalStyles: boolean;
  logos: IThemeLogoPlacements;
  isDarkMode: boolean;
  publicSiteConfig: IPublicSiteConfig;

  // after styled-components update 6.1.17, doesn't accept custom properties in theme
  buttonTabBadgeBorderRadius?: any;
}

export interface IThemeConfig {
  name: string;
  brandColours: BrandColours | MainBrandColours;
  baseColours: BaseColours;
  logos: ITheme['logos'];
  isDarkMode?: boolean;
  eoraColours: IEoraThemeColours;
  publicSiteConfig: IPublicSiteConfig;
}

export type IThemeConfigOverrides = DeepPartial<IThemeConfig>;

export const generateTheme = ({ name, brandColours, baseColours, eoraColours, logos, isDarkMode, publicSiteConfig }: IThemeConfig): ITheme => ({
  name,
  ...generateThemeColours({ brandColours, baseColours, eoraColours }),
  blurs,
  fonts,
  spaces,
  eoraSpaces,
  breakpoints,
  mediaQueries,
  sizes,
  logos,
  shadows: generateShadows(),
  ...generateBorderRadii(),
  // Note: not yet used right now, just putting it here for "just in case scenarios"
  isDarkMode: !!isDarkMode,
  // Note: determines if Global Styles are applied instead of a styled div wrapper
  hasGlobalStyles: true,
  publicSiteConfig
});
